/**
 * PATH: apps/qa/src/pages/area-execution/AreaModals.tsx
 * PURPOSE: ApplyTemplateModal, LockModal, RequestHoldPointModal for area execution
 */

import { useState } from 'react'
import { X, Check, Lock, Unlock, Shield, Loader2, CheckCircle2, ClipboardList, ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Template } from './area-execution-types'

export function ApplyTemplateModal({ isOpen, onClose, onApply, templates, isLoading }: {
  isOpen: boolean; onClose: () => void; onApply: (templateId: string) => void; templates: Template[]; isLoading: boolean
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const safeTemplates = Array.isArray(templates) ? templates : []
  const filtered = safeTemplates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || (t.trade && t.trade.toLowerCase().includes(search.toLowerCase())))

  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2"><ListChecks className="w-5 h-5 text-primary" />Apply Checklist Template</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <input type="text" placeholder="Search templates..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary mb-4" />
        <div className="flex-1 overflow-y-auto space-y-2 mb-4">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground"><ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No templates found</p></div>
          ) : filtered.map(template => (
            <button key={template.id} onClick={() => setSelected(template.id)}
              className={cn('w-full p-3 rounded-lg border text-left transition-all', selected === template.id ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'border-border hover:bg-accent')}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{template.name}</span>
                {selected === template.id && <CheckCircle2 className="w-4 h-4 text-primary" />}
              </div>
              {template.trade && <span className="text-xs text-muted-foreground">{template.trade}</span>}
              {template.item_count && <span className="text-xs text-muted-foreground ml-2">• {template.item_count} items</span>}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={() => selected && onApply(selected)} disabled={!selected || isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Applying...</> : <><Check className="w-4 h-4" />Apply Template</>}
          </button>
        </div>
      </div>
    </div>
  )
}

export function LockModal({ isOpen, isLocked, onClose, onConfirm, isLoading }: {
  isOpen: boolean; isLocked: boolean; onClose: () => void; onConfirm: (reason: string) => void; isLoading: boolean
}) {
  const [reason, setReason] = useState('')
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            {isLocked ? <><Unlock className="w-5 h-5 text-amber-500" />Unlock Area</> : <><Lock className="w-5 h-5 text-emerald-500" />Lock Area</>}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-muted-foreground text-sm mb-4">
          {isLocked ? 'Unlocking will allow further edits to this area. This action will be logged.' : 'Locking will prevent further changes to checklist items in this area.'}
        </p>
        {isLocked && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1.5">Reason for unlocking <span className="text-destructive">*</span></label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why does this area need to be unlocked?"
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={(isLocked && !reason.trim()) || isLoading}
            className={cn('inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              isLocked ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-emerald-500 text-white hover:bg-emerald-600')}>
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {isLocked ? 'Unlock' : 'Lock'} Area
          </button>
        </div>
      </div>
    </div>
  )
}

export function RequestHoldPointModal({ isOpen, onClose, onSubmit, itemTitle, isLoading }: {
  isOpen: boolean; onClose: () => void; onSubmit: (name: string) => void; itemTitle?: string; isLoading: boolean
}) {
  const [name, setName] = useState(itemTitle ? `Hold Point: ${itemTitle}` : '')
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2"><Shield className="w-5 h-5 text-amber-500" />Request Sign-off</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-muted-foreground text-sm mb-4">Request a formal sign-off for this checkpoint. The designated approver will be notified.</p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-1.5">Hold Point Name <span className="text-destructive">*</span></label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Structural inspection complete"
            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={() => onSubmit(name)} disabled={!name.trim() || isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Requesting...</> : <><Shield className="w-4 h-4" />Request Sign-off</>}
          </button>
        </div>
      </div>
    </div>
  )
}
